/**
 * WS-Q Q-7 / AUD-8 IMP-13 — the session cliff, announced before it lands.
 *
 * The refresh chain expires hard: a non-persistent session dies 30 days after login no matter
 * how continuously it has been used, and even a "keep me signed in" chain is capped at 365
 * days from first login. That is a deliberate product rule. What was NOT deliberate is that
 * nothing ever said so: the chain simply stopped refreshing, and the operator met it as a
 * mid-action 401 during whatever they happened to be doing. "It signs me out sometimes", with
 * a 30-day period, is what a monthly cliff looks like from the inside.
 *
 * Q-3 put `chain_expires_at` on the wire — a client cannot warn about a number it has never
 * been told. This decides what to say about it, and, just as importantly, when to say nothing:
 * an older cloud sends no field, and inventing a countdown from a missing value is how a UI
 * starts lying.
 */
import { useEffect, useMemo, useState } from "react";
import { parseServerTs } from "@genz/platform";
import { useSessionStore } from "../stores/session.js";

/** Start warning two days out — long enough to sign in again at a moment of one's choosing. */
export const CHAIN_WARN_HOURS = 48;
/** Inside this, the wording sharpens. Same visual tier; see the banner. */
export const CHAIN_IMMINENT_HOURS = 6;
/**
 * How often the countdown is recomputed. Fifteen minutes: the banner speaks in whole hours,
 * so anything faster re-renders the shell to say the same thing.
 */
export const CHAIN_TICK_MS = 15 * 60 * 1000;

export type ChainWarning =
  | { level: "none" }
  | { level: "soon"; hoursLeft: number }
  | { level: "imminent"; hoursLeft: number };

/**
 * How long this session chain has left, and what to say about it.
 *
 * `null`/`undefined`/unparseable ⇒ `none`: the server did not tell us, so we say nothing.
 * Hours are rounded DOWN, so the banner never promises time that is not there, and clamped at
 * zero, because "expires in -4 hours" is worse than no banner at all.
 */
export function chainWarning(
  chainExpiresAt: string | null | undefined,
  now: number,
): ChainWarning {
  // `parseServerTs`, never `new Date(iso)`: a zone-less cloud timestamp is UTC, and reading it
  // as local time is AUD-4 LB-4 — nine sites, every timestamp shifted by the viewer's offset.
  const expiresAt = parseServerTs(chainExpiresAt);
  if (!expiresAt) return { level: "none" };
  const hoursLeft = Math.max(0, Math.floor((expiresAt - now) / 3_600_000));
  if (expiresAt - now >= CHAIN_WARN_HOURS * 3_600_000) return { level: "none" };
  if (expiresAt - now >= CHAIN_IMMINENT_HOURS * 3_600_000) return { level: "soon", hoursLeft };
  return { level: "imminent", hoursLeft };
}

/**
 * ONE interval for every consumer.
 *
 * A `setInterval` per mounted component is how a shell ends up with a dozen timers all
 * computing the same number; the registry keeps exactly one alive while anybody is watching,
 * and clears it when the last consumer unmounts.
 */
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | undefined;

function subscribeTick(fn: () => void): () => void {
  listeners.add(fn);
  if (timer === undefined) {
    timer = setInterval(() => {
      for (const listener of [...listeners]) listener();
    }, CHAIN_TICK_MS);
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };
}

/**
 * The live warning for the signed-in session. Recomputes when the tokens change (every
 * rotation carries a fresh `chain_expires_at`, and a persistent chain's moves forward) and on
 * the shared tick — not on every render.
 */
export function useChainWarning(): ChainWarning {
  const chainExpiresAt = useSessionStore((s) => s.session?.tokens?.chain_expires_at);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => subscribeTick(() => setNow(Date.now())), []);
  return useMemo(() => chainWarning(chainExpiresAt, now), [chainExpiresAt, now]);
}
